import * as React from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "./icons";

import { cn } from "utils/cn";

export interface Crumb {
  label: React.ReactNode;
  /** Omit on the last crumb (the current page). */
  to?: string;
}

export interface PageHeaderProps {
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Trail above the title for detail pages, e.g. Tenants › Apollo Hospitals. */
  breadcrumbs?: Crumb[];
  /** Primary and secondary page actions, right-aligned. */
  actions?: React.ReactNode;
  /** Small elements next to the title, e.g. a StatusBadge. */
  meta?: React.ReactNode;
  className?: string;
  children?: React.ReactNode;
}

/** Title block at the top of every page. `children` renders under it (tabs, filters). */
export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  description,
  breadcrumbs,
  actions,
  meta,
  className,
  children,
}) => (
  <header data-ui="page-header" className={cn("space-y-4", className)}>
    {breadcrumbs && breadcrumbs.length > 0 && (
      <nav aria-label="Breadcrumb">
        <ol className="flex flex-wrap items-center gap-1 text-sm text-muted-foreground">
          {breadcrumbs.map((crumb, index) => (
            <li key={index} className="flex items-center gap-1">
              {index > 0 && (
                <ChevronRight aria-hidden="true" className="h-3.5 w-3.5 text-subtle-foreground" />
              )}
              {crumb.to ? (
                <Link
                  to={crumb.to}
                  className="rounded-sm transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {crumb.label}
                </Link>
              ) : (
                <span aria-current="page" className="text-foreground">
                  {crumb.label}
                </span>
              )}
            </li>
          ))}
        </ol>
      </nav>
    )}
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div className="min-w-0 space-y-1.5">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">{title}</h1>
          {meta}
        </div>
        {description && <p className="max-w-2xl text-sm text-muted-foreground">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
    {children}
  </header>
);

export interface PageProps extends React.HTMLAttributes<HTMLDivElement> {
  /** `wide` for data-heavy pages (tables), `narrow` for forms and settings. */
  width?: "narrow" | "default" | "wide";
}

/** Content container with the shell's standard padding and max width. */
export const Page: React.FC<PageProps> = ({ width = "default", className, ...props }) => (
  <div
    data-ui="page"
    className={cn(
      "mx-auto w-full space-y-6 px-4 py-6 sm:px-6 lg:px-8 lg:py-8",
      width === "narrow" && "max-w-3xl",
      width === "default" && "max-w-6xl",
      width === "wide" && "max-w-[96rem]",
      className
    )}
    {...props}
  />
);
